// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TextFieldProps } from "@guestsupply/MobiusOverrides/TextField/TextField";
import { color, xScreen } from "@guestsupply/XDL/Utilities";
import { hideElement } from "@guestsupply/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { TagPresentationProps } from "@insite/mobius/Tag";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface MyAccountFilterSectionStyles {
    filterContainer?: GridContainerProps;
    countGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    searchFormStyles: {
        wrapper?: InjectableCss;
        headingText?: TypographyPresentationProps;
    };
    clearButton?: ButtonPresentationProps;
    datesGridItem?: GridItemProps;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    smallGridItem?: GridItemProps;
    mediumGridItem?: GridItemProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFilterTag?: TagPresentationProps;
    orderCountText?: TypographyProps;
}

const myAccountFilterSectionStyles: MyAccountFilterSectionStyles = {
    filterContainer: {
        gap: 8,
        css: css`
            margin-bottom: 0;
        `,
    },
    countGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            font-size: 14px;
            font-weight: bold;
            align-items: center;
            text-transform: capitalize;
            order: 2;

            &,
            & > div {
                text-align: right;
                justify-content: flex-end;
            }

            ${xScreen("lte", "xs")} {
                order: 1;
                &,
                & > div {
                    text-align: left;
                    justify-content: flex-start;
                }
            }
        `,
    },
    toggleFilterGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            order: 1;
            ${xScreen("lte", "xs")} {
                order: 2;
            }
        `,
    },
    toggleFilterButton: {
        shape: "rectangle",
        css: css`
            min-width: 120px;
            height: 55px;
            display: flex;
            align-items: center;
            padding: 10px 15px;
            border: 1px solid ${color("common.border")};
            border-top: 5px solid ${color("primary")};
            justify-content: center;
            font-size: 18px;
            background: ${color("common.white")};
            margin: 0px;
            color: ${color("text.main")};
            border-radius: 0;
            ${({ filtersOpen }: any) => filtersOpen && "border-bottom : none;"}

            &:before {
                content: "Filter";
                font-weight: bold;
            }

            &:focus,
            &:active {
                &&&& {
                    outline: none;
                    background: ${color("common.white")};
                    border-color: ${color("common.border")};
                    ${IconWrapper} {
                        color: ${color("common.ui3")};
                    }
                }
            }
            &:hover {
                &&&& {
                    outline: none;
                    background: ${color("common.white")};
                    border-color: ${color("common.border")};
                    border-top: 5px solid ${color("secondary")};
                    ${IconWrapper} {
                        color: ${color("secondary")};
                    }
                }
            }
        `,
    },
    toggleFilterButtonIcon: {
        color: "primary",
        size: 20,
        css: css`
            &&& {
                margin-left: 10px;
                color: ${color("primary")};
                top: unset;
            }
        `,
    },
    searchFormStyles: {
        wrapper: {
            css: css`
                background: ${color("common.white")};
                border: 1px solid ${color("common.border")};
                padding: 30px 30px 15px;
                position: relative;
                &:before {
                    content: " ";
                    width: 118px;
                    height: 3px;
                    position: absolute;
                    top: -2px;
                    background: ${color("common.white")};
                    left: 0;
                }

                ${GridItemStyle} {
                    align-items: end;
                }
            `,
        },
        headingText: {
            css: css`
                ${hideElement}
            `,
        },
    },
    clearButton: {
        variant: "secondary",
        css: css`
            float: right;
            margin-top: 5px;
        `,
        mergeCss: true,
    },
    orderTotalGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding: 10px 5px;
            ${GridItemStyle} {
                padding: 10px;
            }
        `,
    },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0;
            `,
        },
        label: translate("Amount"),
    } as TextFieldProps,
    datesGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding: 10px 5px;
            ${xScreen("lte", "sm")} {
                padding-top: 0;
            }

            ${GridItemStyle} {
                padding: 10px;
                > div {
                    width: 100%;
                }
            }
        `,
    },
    mediumGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    smallGridItem: {
        width: [12, 6, 3, 3, 2],
        css: css`
            > div {
                width: 100%;
                display: flex;
                padding-top: 2px;
            }
        `,
    },
    datePickersWrapper: {
        css: css`
            display: flex;
            > div {
                width: 50%;
            }
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                > div {
                    width: auto;
                }
            }
        `,
    },
    fromDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-right: 10px;
                ${xScreen("lte", "xs")} {
                    padding-right: 0;
                }
            `,
            formInputWrapper: css`
                [class^="DateTimePickerStyle"] {
                    height: 36px;
                }
            `,
        },
    },
    toDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-left: 10px;
                ${xScreen("lte", "xs")} {
                    padding-left: 0;
                }
            `,
            formInputWrapper: css`
                [class^="DateTimePickerStyle"] {
                    height: 36px;
                }
            `,
        },
        labelProps: {
            color: "common.background",
        },
    },
    appliedFilterTag: {
        css: css`
            margin-top: 25px;
        `,
    },
    // XNG-Changes: GSS-343
    orderCountText: {
        variant: "h4",
        color: "primary",
        as: "p",
        css: css`
            margin: 0;
            white-space: nowrap;
        `,
    },
};

export default myAccountFilterSectionStyles;
